/**
 * `Equipment` — page `/materiel` avec 2 onglets :
 *   - Catalogue : grille des prix du matériel (CRUD admin+coord)
 *   - Bons      : liste globale des bons de remise, filtres, PDF
 *
 * iter110n · CEO 2026-09-27 · Bon de Remise Électronique.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Package, Plus, Pencil, Trash2, FileDown, ClipboardCheck, Search, UserPlus, X, Send } from "lucide-react";
import DeliveryNoteModal from "../components/DeliveryNoteModal";
import { api } from "../lib/api";
import { openAuthedPdf } from "../lib/pdf";
import { useAuth } from "../lib/auth";

const STATUS_LABELS = {
    draft: "Brouillon",
    awaiting_recipient: "Attente salarié",
    awaiting_deliverer: "Attente remettant",
    signed: "Signé",
    voided: "Annulé",
};
const STATUS_COLORS = {
    draft: "bg-slate-100 text-slate-700",
    awaiting_recipient: "bg-amber-100 text-amber-800",
    awaiting_deliverer: "bg-amber-100 text-amber-800",
    signed: "bg-emerald-100 text-emerald-800",
    voided: "bg-red-100 text-red-800",
};
const TYPE_LABELS = {
    initial: "Remise initiale",
    complementary: "Complémentaire",
    return: "Restitution",
    regularization: "Régularisation",
};

export default function Equipment() {
    const { user } = useAuth();
    const canManageCatalog = ["admin", "coordinateur"].includes(user?.role);

    return (
        <div className="p-6">
            <div className="flex items-center gap-2 mb-6">
                <Package className="w-6 h-6 text-orange-500" />
                <h1 className="text-2xl font-black text-slate-900">Matériel & bons de remise</h1>
            </div>
            <Tabs defaultValue="notes" className="w-full">
                <TabsList className="mb-4">
                    <TabsTrigger value="notes" data-testid="equipment-tab-notes">Bons de remise</TabsTrigger>
                    <TabsTrigger value="catalog" data-testid="equipment-tab-catalog">Catalogue</TabsTrigger>
                </TabsList>
                <TabsContent value="notes"><NotesTab /></TabsContent>
                <TabsContent value="catalog"><CatalogTab canWrite={canManageCatalog} /></TabsContent>
            </Tabs>
        </div>
    );
}

// ═════════════════════ Bons de remise tab ═════════════════════
function NotesTab() {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");
    // CEO 2026-10-07 · Picker salarié + modal création depuis /materiel
    const [showPicker, setShowPicker] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState(null); // {subject_type, subject_id, label}

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/equipment/notes", {
                params: statusFilter ? { status: statusFilter } : {},
            });
            setNotes(data.items || []);
        } catch (err) {
            toast.error("Impossible de charger les bons");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = useMemo(() => {
        if (!search.trim()) return notes;
        const q = search.toLowerCase();
        return notes.filter((n) => n.subject_full_name?.toLowerCase().includes(q));
    }, [notes, search]);

    const downloadPdf = (noteId) => openAuthedPdf(`/equipment/notes/${noteId}/pdf`);

    return (
        <div className="space-y-3">
            <div className="flex gap-2 flex-wrap items-center">
                <Input
                    placeholder="Rechercher par salarié..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="max-w-xs"
                    data-testid="equipment-notes-search"
                />
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-9 px-3 rounded-md border border-slate-300 text-sm bg-white"
                    data-testid="equipment-notes-status-filter"
                >
                    <option value="">Tous les statuts</option>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                    ))}
                </select>
                {/* CEO 2026-10-07 · Bouton principal « Nouveau bon » directement
                    depuis /materiel — avant il fallait passer par la fiche
                    du salarié, pas intuitif. */}
                <button
                    onClick={() => setShowPicker(true)}
                    data-testid="equipment-new-note-btn"
                    className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-shariti-orange text-white px-4 h-9 font-extrabold text-xs hover:bg-shariti-orange/90 transition"
                >
                    <Plus className="h-4 w-4" />
                    Nouveau bon de remise
                </button>
            </div>

            {loading ? (
                <div className="text-sm text-slate-500 text-center py-8">Chargement…</div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                    <ClipboardCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <div>Aucun bon.</div>
                    <div className="text-xs mt-1">Crée un bon depuis la fiche d'un salarié ou d'un PI.</div>
                </div>
            ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left">
                            <tr>
                                <th className="px-3 py-2 font-semibold">Salarié</th>
                                <th className="px-3 py-2 font-semibold">Type</th>
                                <th className="px-3 py-2 font-semibold">Articles</th>
                                <th className="px-3 py-2 font-semibold text-right">Valeur</th>
                                <th className="px-3 py-2 font-semibold">Statut</th>
                                <th className="px-3 py-2 font-semibold">Date</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((n) => (
                                <tr key={n.id} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="px-3 py-2 font-medium">{n.subject_full_name}</td>
                                    <td className="px-3 py-2">{TYPE_LABELS[n.type]}</td>
                                    <td className="px-3 py-2 text-slate-600">{n.items?.length || 0}</td>
                                    <td className="px-3 py-2 text-right font-mono">{n.total_value_eur?.toFixed(2)} €</td>
                                    <td className="px-3 py-2">
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[n.status]}`}>
                                            {STATUS_LABELS[n.status]}
                                        </span>
                                        {n.status === "awaiting_recipient" && n.remote_sign && (
                                            <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`equipment-note-channel-${n.id}`}>
                                                {n.remote_sign.channel === "email" ? `Lien envoyé · ${n.remote_sign.email}` : `Espace Prérec · ${n.remote_sign.app_user_name || ""}`}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-500">
                                        {(n.signed_at || n.created_at || "").slice(0, 10)}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <NoteActions
                                            note={n}
                                            onPdf={() => downloadPdf(n.id)}
                                            onComplete={() => setSelectedSubject({
                                                subject_type: n.subject_type,
                                                subject_id: n.subject_id,
                                                label: n.subject_full_name,
                                                defaultType: "complementary",
                                            })}
                                            onResent={load}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* CEO 2026-10-07 · Picker salarié depuis /materiel */}
            {showPicker && (
                <SubjectPickerModal
                    onClose={() => setShowPicker(false)}
                    onPick={(subject) => {
                        setShowPicker(false);
                        setSelectedSubject(subject);
                    }}
                />
            )}

            {/* Modal création de bon réutilise le composant existant */}
            {selectedSubject && (
                <DeliveryNoteModal
                    open={!!selectedSubject}
                    onOpenChange={(v) => { if (!v) setSelectedSubject(null); }}
                    onCompleted={() => load()}
                    subjectType={selectedSubject.subject_type}
                    subjectId={selectedSubject.subject_id}
                    subjectName={selectedSubject.label}
                    defaultType={selectedSubject.defaultType || "initial"}
                />
            )}
        </div>
    );
}


// Actions d'une ligne : Compléter (signé), Relancer (signature à distance), PDF.
function NoteActions({ note, onPdf, onComplete, onResent }) {
    const [busy, setBusy] = useState(false);
    const resend = async () => {
        setBusy(true);
        try {
            await api.post(`/equipment/notes/${note.id}/request-signature`, { channel: note.remote_sign.channel });
            toast.success("Demande de signature renvoyée");
            onResent?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Relance impossible");
        } finally {
            setBusy(false);
        }
    };
    const btn = "inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-extrabold transition";
    return (
        <div className="inline-flex items-center gap-1.5 justify-end">
            {note.status === "signed" && note.type !== "return" && (
                <button onClick={onComplete} className={`${btn} bg-shariti-orange-soft text-shariti-orange hover:bg-shariti-orange hover:text-white`}
                    title="Ajouter du matériel à cette remise" data-testid={`equipment-note-complete-${note.id}`}>
                    <Plus className="w-3.5 h-3.5" /> Compléter
                </button>
            )}
            {note.status === "awaiting_recipient" && note.remote_sign && (
                <button onClick={resend} disabled={busy} className={`${btn} bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-50`}
                    data-testid={`equipment-note-resend-${note.id}`}>
                    <Send className="w-3.5 h-3.5" /> Relancer
                </button>
            )}
            {note.status === "signed" && (
                <button onClick={onPdf} className="text-orange-600 hover:text-orange-700 p-1" title="Télécharger le PDF"
                    data-testid={`equipment-note-pdf-${note.id}`}>
                    <FileDown className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}

// ═════════════════════ Subject picker modal ═════════════════════
// CEO 2026-10-07 · Autocomplete sur /equipment/subjects/search pour
// permettre de créer un bon directement depuis /materiel.
function SubjectPickerModal({ onClose, onPick }) {
    const PAGE = 30;
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const debounceRef = useRef(null);
    const reqRef = useRef(0);

    const fetchPage = async (offset) => {
        const reqId = ++reqRef.current;
        const { data } = await api.get("/equipment/subjects/search", {
            params: { q: query, offset, limit: PAGE },
        });
        if (reqId !== reqRef.current) return;
        setResults((prev) => (offset === 0 ? data.items || [] : [...prev, ...(data.items || [])]));
        setTotal(data.total || 0);
        setHasMore(!!data.has_more);
    };

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                await fetchPage(0);
            } catch (e) {
                setResults([]);
                setTotal(0);
                setHasMore(false);
            } finally {
                setLoading(false);
            }
        }, 280);
        return () => clearTimeout(debounceRef.current);
    }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

    const onScroll = async (e) => {
        const el = e.currentTarget;
        if (!hasMore || loadingMore || loading) return;
        if (el.scrollTop + el.clientHeight < el.scrollHeight - 120) return;
        setLoadingMore(true);
        try {
            await fetchPage(results.length);
        } catch (err) {
            toast.error("Impossible de charger la suite de la liste");
        } finally {
            setLoadingMore(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="subject-picker-modal">
            <div className="bg-white rounded-3xl w-full max-w-2xl my-8 shadow-xl">
                <header className="flex items-center justify-between px-6 py-4 border-b border-shariti-gray">
                    <h2 className="text-lg font-extrabold inline-flex items-center gap-2">
                        <UserPlus className="h-5 w-5 text-shariti-orange" />
                        Choisir le destinataire du bon
                    </h2>
                    <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-shariti-cream flex items-center justify-center" data-testid="subject-picker-close">
                        <X className="h-4 w-4" />
                    </button>
                </header>
                <div className="p-5 space-y-3">
                    <div className="relative">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Nom, prénom ou code RD du salarié…"
                            className="pl-9"
                            data-testid="subject-picker-input"
                        />
                    </div>
                    <p className="text-[11px] text-slate-500 font-medium flex justify-between gap-2">
                        <span>La liste n'affiche que les salariés de votre périmètre (contrat signé · votre équipe ou vos zones).</span>
                        {!loading && total > 0 && (
                            <span className="font-bold text-slate-600 whitespace-nowrap" data-testid="subject-picker-count">
                                {results.length} sur {total}
                            </span>
                        )}
                    </p>
                    <div onScroll={onScroll} className="max-h-[50vh] overflow-y-auto divide-y divide-shariti-cream rounded-2xl border border-shariti-gray" data-testid="subject-picker-list">
                        {loading ? (
                            <div className="py-8 text-center text-slate-400 text-sm">Recherche…</div>
                        ) : results.length === 0 ? (
                            <div className="py-10 text-center text-slate-400 text-sm">
                                {query ? "Aucun salarié trouvé." : "Aucun salarié dans votre périmètre."}
                            </div>
                        ) : (
                            results.map((r) => (
                                <button
                                    key={`${r.subject_type}-${r.subject_id}`}
                                    type="button"
                                    onClick={() => onPick(r)}
                                    data-testid={`subject-picker-item-${r.subject_id}`}
                                    className="w-full text-left px-4 py-3 hover:bg-shariti-cream/50 flex items-center gap-3 transition"
                                >
                                    <div className={`h-9 w-9 rounded-2xl flex items-center justify-center text-xs font-black ${r.subject_type === "permanent" ? "bg-indigo-100 text-indigo-700" : "bg-shariti-orange-soft text-shariti-orange"}`}>
                                        {(r.label[0] || "?").toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-bold truncate">
                                            {r.label}
                                            {r.code_rd && (
                                                <span className="ml-2 text-[10px] font-mono text-slate-400 uppercase">
                                                    {r.code_rd}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-slate-500 truncate">
                                            {r.sub}
                                            {r.region && (
                                                <span className="ml-2 inline-block px-1.5 py-0.5 rounded-md bg-shariti-cream text-[10px] font-bold">
                                                    {r.region}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            ))
                        )}
                        {loadingMore && (
                            <div className="py-3 text-center text-xs text-slate-400" data-testid="subject-picker-loading-more">Chargement…</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ═════════════════════ Catalogue tab ═════════════════════
function CatalogTab({ canWrite }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/equipment/catalog", { params: { include_inactive: true } });
            setItems(data.items || []);
        } catch (err) {
            toast.error("Impossible de charger le catalogue");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Retirer cet article du catalogue ? (soft delete — l'historique est préservé)")) return;
        try {
            await api.delete(`/equipment/catalog/${id}`);
            toast.success("Article retiré du catalogue");
            load();
        } catch (err) {
            toast.error("Erreur de suppression");
        }
    };

    return (
        <div className="space-y-3">
            {canWrite && (
                <div className="flex justify-end">
                    <Button
                        onClick={() => { setEditing(null); setEditorOpen(true); }}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid="equipment-catalog-new"
                    >
                        <Plus className="w-4 h-4 mr-1" /> Nouvel article
                    </Button>
                </div>
            )}
            {loading ? (
                <div className="text-sm text-slate-500 text-center py-8">Chargement…</div>
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                    <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <div>Catalogue vide.</div>
                    <div className="text-xs mt-1">Ajoute les articles standards remis aux salariés.</div>
                </div>
            ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left">
                            <tr>
                                <th className="px-3 py-2 font-semibold">Article</th>
                                <th className="px-3 py-2 font-semibold">Catégorie</th>
                                <th className="px-3 py-2 font-semibold text-right">Valeur unitaire</th>
                                <th className="px-3 py-2 font-semibold">Options</th>
                                <th className="px-3 py-2 font-semibold">Actif</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => (
                                <tr key={it.id} className={`border-t border-slate-100 hover:bg-slate-50 ${!it.active ? "opacity-50" : ""}`}>
                                    <td className="px-3 py-2 font-medium">{it.name}</td>
                                    <td className="px-3 py-2 text-slate-600">{it.category || "—"}</td>
                                    <td className="px-3 py-2 text-right font-mono">{it.unit_value_eur.toFixed(2)} €</td>
                                    <td className="px-3 py-2 text-xs text-slate-500">
                                        {it.needs_size && "Taille "}
                                        {it.needs_serial && "N° série"}
                                        {!it.needs_size && !it.needs_serial && "—"}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                            it.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-500"
                                        }`}>
                                            {it.active ? "Actif" : "Archivé"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {canWrite && (
                                            <div className="flex gap-1 justify-end">
                                                <button
                                                    onClick={() => { setEditing(it); setEditorOpen(true); }}
                                                    className="text-slate-600 hover:text-orange-600"
                                                    data-testid={`equipment-catalog-edit-${it.id}`}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                {it.active && (
                                                    <button
                                                        onClick={() => remove(it.id)}
                                                        className="text-slate-600 hover:text-red-600"
                                                        data-testid={`equipment-catalog-delete-${it.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <CatalogEditor
                open={editorOpen}
                onOpenChange={setEditorOpen}
                editing={editing}
                onSaved={() => { setEditorOpen(false); load(); }}
            />
        </div>
    );
}

function CatalogEditor({ open, onOpenChange, editing, onSaved }) {
    const [name, setName] = useState("");
    const [category, setCategory] = useState("");
    const [unitValueEur, setUnitValueEur] = useState("0");
    const [needsSerial, setNeedsSerial] = useState(false);
    const [needsSize, setNeedsSize] = useState(false);
    const [sizes, setSizes] = useState("");
    // iter110n-2 · CEO 2026-09-28 · variante (association) + usage partagé
    const [variantKind, setVariantKind] = useState("");
    const [variantOptions, setVariantOptions] = useState("");
    const [allowSharedUse, setAllowSharedUse] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName(editing?.name || "");
            setCategory(editing?.category || "");
            setUnitValueEur(String(editing?.unit_value_eur ?? "0"));
            setNeedsSerial(!!editing?.needs_serial);
            setNeedsSize(!!editing?.needs_size);
            setSizes((editing?.sizes || []).join(", "));
            setVariantKind(editing?.variant_kind || "");
            setVariantOptions((editing?.variant_options || []).join("\n"));
            setAllowSharedUse(!!editing?.allow_shared_use);
        }
    }, [open, editing]);

    const save = async () => {
        if (!name.trim()) { toast.error("Nom requis"); return; }
        setSaving(true);
        try {
            const payload = {
                name: name.trim(),
                category: category.trim() || null,
                unit_value_eur: parseFloat(unitValueEur) || 0,
                needs_serial: needsSerial,
                needs_size: needsSize,
                sizes: needsSize ? sizes.split(",").map((s) => s.trim()).filter(Boolean) : [],
                variant_kind: variantKind || null,
                variant_options: variantKind
                    ? variantOptions.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean)
                    : [],
                allow_shared_use: allowSharedUse,
            };
            if (editing?.id) {
                await api.put(`/equipment/catalog/${editing.id}`, payload);
                toast.success("Article mis à jour");
            } else {
                await api.post("/equipment/catalog", payload);
                toast.success("Article ajouté au catalogue");
            }
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="equipment-catalog-editor">
                <DialogHeader>
                    <DialogTitle>{editing ? "Modifier l'article" : "Nouvel article"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 py-2">
                    <div>
                        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Désignation</label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="catalog-editor-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Catégorie</label>
                            <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Vêtements, Tablettes..." />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Valeur unitaire (€)</label>
                            <Input type="number" min="0" step="0.01" value={unitValueEur} onChange={(e) => setUnitValueEur(e.target.value)} data-testid="catalog-editor-value" />
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={needsSerial} onChange={(e) => setNeedsSerial(e.target.checked)} className="w-4 h-4 accent-orange-500" />
                            N° de série requis
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={needsSize} onChange={(e) => setNeedsSize(e.target.checked)} className="w-4 h-4 accent-orange-500" />
                            Taille à préciser
                        </label>
                        <label className="flex items-center gap-2 text-sm" title="Autorise le choix 'Usage partagé' au moment de la remise — l'article reste dans l'inventaire du responsable d'équipe.">
                            <input type="checkbox" checked={allowSharedUse} onChange={(e) => setAllowSharedUse(e.target.checked)} className="w-4 h-4 accent-orange-500" data-testid="catalog-editor-allow-shared" />
                            Usage partagé possible
                        </label>
                    </div>
                    {needsSize && (
                        <div>
                            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Tailles disponibles (séparées par virgules)</label>
                            <Input value={sizes} onChange={(e) => setSizes(e.target.value)} placeholder="XS, S, M, L, XL" />
                        </div>
                    )}
                    {/* Variant kind (only "association" for now) */}
                    <div>
                        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Variante par ligne (optionnel)</label>
                        <select
                            value={variantKind}
                            onChange={(e) => setVariantKind(e.target.value)}
                            className="w-full h-9 px-2 rounded-md border border-slate-300 bg-white text-sm"
                            data-testid="catalog-editor-variant-kind"
                        >
                            <option value="">Aucune</option>
                            <option value="association">Choix d'association (dropdown)</option>
                        </select>
                    </div>
                    {variantKind === "association" && (
                        <div>
                            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Associations proposées (une par ligne)</label>
                            <textarea
                                value={variantOptions}
                                onChange={(e) => setVariantOptions(e.target.value)}
                                rows={6}
                                placeholder={"Amnesty International\nCCFD-Terre Solidaire\nHandicap International\n..."}
                                className="w-full text-sm p-2 rounded-md border border-slate-300 font-mono"
                                data-testid="catalog-editor-variant-options"
                            />
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                    <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="catalog-editor-save">
                        {saving ? "…" : editing ? "Enregistrer" : "Créer"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
